import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  isoFromDate,
  resolveConfigPreset,
  resolveRelative,
} from "./DateRangePicker";
import {
  buildDomain,
  DualDateSlider,
  nearestIndex,
  type DateSliderStep,
} from "./date-slider-core";
import type { DatePreset } from "./types";

// Barrel compat — GenericFilterBar and the package index import the
// step type from this module (it predates ``date-slider-core``).
export type { DateSliderStep };

/**
 * Dual-handle date-range slider — the ``filter_type: "date_slider"``
 * widget. Independent from ``DateRangePicker`` (which stays untouched);
 * the two are alternative pickers for the SAME wire format.
 *
 * The snapped-domain construction, index math and the accessible
 * dual-range-input pair live in ``date-slider-core.tsx`` — shared with
 * the slider row embedded inside the ``DateRangePicker`` popover, so
 * the two hosts can never drift. This file keeps what is standalone-
 * specific: decoding the wire value into handle positions, the local
 * drag state, and the commit-on-release contract.
 *
 * ── Value contract (THE delicate part) ─────────────────────────────
 * On commit the slider emits ``{"start": domain[lo], "end": domain[hi]}``
 * with BOTH dates as plain local-day ISO strings — byte-identical to
 * what ``DateRangePicker.encodeFixed`` emits when the user clicks the
 * same two dates on the dual calendar. The engine's
 * ``_decode_date_filter`` treats a 10-char ``end`` as an INCLUSIVE day
 * and pushes the exclusive bound one day forward (``end + 1d`` →
 * ``col < end``), so the same visual selection produces the exact same
 * SQL window through either widget. Do NOT emit exclusive ends here.
 *
 * Commit timing: the JSON is pushed upstream ONLY on pointerup / keyup /
 * blur — during a drag only the labels update, so the dashboard doesn't
 * refetch on every intermediate step.
 */

export interface DateRangeSliderProps {
  value: string;
  onChange: (value: string) => void;
  /** ``[lo_iso, hi_iso]`` from filter options; empty/undefined → disabled. */
  bounds?: readonly string[];
  step?: DateSliderStep;
  /** Per-report preset library — used only to position handles when the
   *  current value is a ``{"preset": id}`` shape. */
  presets?: readonly DatePreset[] | null;
  disabled?: boolean;
  embedded?: boolean;
}

/**
 * Decode the on-the-wire filter value to a display window ``{start, end}``
 * (both inclusive local-day ISO). Mirrors ``DateRangePicker.decodeValue``:
 *
 * - ``{"start","end"}`` → used directly (nearest-clamped by the caller).
 * - ``{"preset": id}`` / ``{"relative": {...}}`` → resolved via the
 *   exported picker resolvers. These follow the picker's DISPLAY
 *   convention (approximate for exclusive-end presets) — they only
 *   position handles; the engine still resolves the untouched value
 *   exactly. Once the user drags and commits, the value becomes an
 *   explicit ``{"start","end"}`` with the exact contract above.
 * - anything else (empty / malformed / unknown preset) → ``null`` →
 *   handles rest at the domain extremes.
 */
function decodeToWindow(
  raw: string,
  presets: readonly DatePreset[] | null | undefined,
): { start: string; end: string } | null {
  if (!raw) return null;
  let obj: Record<string, unknown>;
  try {
    obj = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (!obj || typeof obj !== "object") return null;
  if (typeof obj.start === "string" && typeof obj.end === "string") {
    return { start: obj.start, end: obj.end };
  }
  if (typeof obj.preset === "string" && presets && presets.length > 0) {
    const resolved = resolveConfigPreset(obj.preset, presets);
    if (resolved) {
      return { start: isoFromDate(resolved[0]), end: isoFromDate(resolved[1]) };
    }
    return null;
  }
  if (obj.relative && typeof obj.relative === "object") {
    const rel = obj.relative as { unit?: string; length?: number };
    if (
      (rel.unit === "days" || rel.unit === "weeks" || rel.unit === "months") &&
      typeof rel.length === "number"
    ) {
      const [start, end] = resolveRelative(rel.unit, rel.length);
      return { start: isoFromDate(start), end: isoFromDate(end) };
    }
  }
  return null;
}

// ── Component ────────────────────────────────────────────

export function DateRangeSlider({
  value,
  onChange,
  bounds,
  step = "day",
  presets,
  disabled,
  embedded,
}: DateRangeSliderProps): JSX.Element {
  const { t } = useTranslation();

  const domain = useMemo(() => buildDomain(bounds, step), [bounds, step]);
  const maxIdx = domain.length - 1;

  // Handle indices derived from the external value (nearest-clamp).
  const derived = useMemo<[number, number]>(() => {
    if (domain.length === 0) return [0, 0];
    const window = decodeToWindow(value, presets);
    if (!window) return [0, maxIdx];
    let lo = nearestIndex(domain, window.start);
    let hi = nearestIndex(domain, window.end);
    if (lo > hi) [lo, hi] = [hi, lo];
    return [lo, hi];
  }, [domain, maxIdx, value, presets]);

  // Local drag state — displayed while interacting, committed upstream
  // only on pointerup / keyup / blur.
  const [pending, setPending] = useState<[number, number] | null>(null);
  useEffect(() => {
    setPending(null);
  }, [value, domain]);

  const [lo, hi] = pending ?? derived;

  const commit = (): void => {
    if (pending === null || domain.length === 0) return;
    const [pLo, pHi] = pending;
    onChange(
      // End is the INCLUSIVE selected day — the engine adds +1 day for
      // its exclusive ``col < end`` bound (same as the calendar picker).
      JSON.stringify({ start: domain[pLo], end: domain[pHi] }),
    );
    setPending(null);
  };

  // Empty / unusable domain → disabled placeholder (mirrors the
  // DateRangePicker's disabled trigger so the bar layout doesn't shift).
  if (domain.length === 0 || disabled) {
    const text =
      domain.length === 0 ? t("dateslider.noData") : `${domain[lo]} → ${domain[hi]}`;
    return (
      <div
        className={`flex h-7 w-full cursor-not-allowed items-center rounded-lg border px-2 text-xs font-medium opacity-60 ${
          embedded
            ? "border-white/20 bg-white/10 text-white/50"
            : "border-neutral-300 bg-neutral-50 text-neutral-500"
        }`}
      >
        <span className="truncate">{text}</span>
      </div>
    );
  }

  return (
    <DualDateSlider
      domain={domain}
      lo={lo}
      hi={hi}
      labels="selection"
      embedded={Boolean(embedded)}
      onChange={(nextLo, nextHi) => setPending([nextLo, nextHi])}
      onCommit={commit}
    />
  );
}
